// ── ItemDetailPanel ──────────────────────────────────────────────────────────
// Shared "full detail" panel for a task/event/reminder — used by BOTH the
// calendar view (day/month/year "tap an item" flow) and the list view (the
// chevron button on a list card). Previously these were two separate
// components that had drifted apart (list view's was missing subtasks,
// priority tag, embedded links, category icon, and the mark-as-passed
// toggle) — this is now the single source of truth for that content so they
// can never diverge again.
import React from "react";
import { createPortal } from "react-dom";
import {
  X, Bell, CalendarDays, CheckSquare, Clock, Tag,
  RefreshCw,
} from "lucide-react";
import { MapPin } from "../../PhosphorIcons";
import { S, DETAIL_BACKDROP } from "../../theme/sharedStyles";
import { PRIORITY_COLORS, DEFAULT_EVENT_COLOR } from "../../constants/palettes";
import { CategoryIcon } from "../../components/CategoryIcon";
import { AttachmentViewer } from "../../components/attachments/AttachmentViewer";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { EmbedPill } from "../../components/EmbedPill";
import { durationLabel } from "../../utils/reminderFormat";
import { isTaskDoneOn, toggleTaskDoneOn } from "../../utils/taskLogic";
import { ReminderToggleRow } from "../../components/ReminderToggleRow";
import { setItemRemindersOn } from "../../utils/itemReminderToggle";
import { ItemActionButtons } from "./ItemActionButtons";
import { MarkUpcomingPanel } from "./MarkUpcomingPanel";
import { holdSheetEnter } from "../../utils/sheetEnter";
import { pauseNavBlur } from "../../utils/navBlur";
import { isEventPassed, eventStatusTap, applyKeepUpcoming, applyMoveEvent } from "../../utils/eventStatus";
import { isGoogleTaskEvent, googleTaskUrl } from "../../google/googleTask";
import { GoogleTaskLink, GoogleTaskDeleteNotice } from "./GoogleTaskLink";

export function ItemDetailPanel({
  item, type, date,
  categories = [],
  tasks = [], calEvents = [], calReminders = [],
  setTasks, setCalEvents, setCalReminders,
  taskOccurrenceOverrides = {}, setTaskOccurrenceOverrides = () => {},
  calEventOccurrenceOverrides = {}, setCalEventOccurrenceOverrides = () => {}, weekStart = "sunday",
  onEdit = () => {}, onSkip = () => {}, onDelete = () => {}, onClose = () => {},
  zIndexBase = 2000,
  animate = true,
}) {
  // Delete on a Google Task shows a notice instead (see GoogleTaskLink.jsx).
  // Above the early return so the hook always runs.
  const [gTaskNotice, setGTaskNotice] = React.useState(false);
  const [upcomingFor, setUpcomingFor] = React.useState(null); // event whose Mark as upcoming panel is open
  // No frosted-bar re-blur behind the opening pop (navBlur.js).
  React.useEffect(() => { if (animate) pauseNavBlur(600); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item || !type) return null;

  const cardStyle = {
    background: "var(--c-bg-card)", borderRadius: 22, padding: "16px 20px 22px",
    width: "100%", maxWidth: 420, boxShadow: "0 14px 50px rgba(212,120,154,0.28)",
    maxHeight: "85vh", overflowY: "auto", fontFamily: "'Quicksand', sans-serif",
  };

  const eventColor = (ev) => {
    if (ev.categoryId) {
      const cat = categories.find(c => c.id === ev.categoryId);
      if (cat?.color) return cat.color;
    }
    return ev.color || DEFAULT_EVENT_COLOR;
  };

  // Top row: big type label (the hero) + close button
  const topRow = (Icon, label, accentColor) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: "var(--c-text)", letterSpacing: 0.3 }}>{label}</span>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 9, background: accentColor + "18", color: accentColor, flexShrink: 0 }}>
          <Icon size={16} />
        </span>
      </div>
      <button onClick={onClose}
        style={{ background: "var(--c-bg-soft)", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", color: "var(--c-text-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={15} /></button>
    </div>
  );


  const metaRow = (Icon, text) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600 }}>
      <span style={{ flexShrink: 0, marginTop: 1, color: "var(--c-text-soft)", display: "inline-flex" }}><Icon size={14} /></span>
      <span style={{ wordBreak: "break-word", lineHeight: 1.4 }}>{text}</span>
    </div>
  );

  // Notes can contain "rn-check" checklist rows (same markup the
  // RichNotesEditor inserts) — click-to-toggle them, same logic as the
  // editor's own handleEditorClick, then persist the updated HTML.
  const handleNotesClick = (e, onSave) => {
    const box = e.target.closest && e.target.closest(".rn-check-box");
    if (!box || !onSave) return;
    const label = box.parentElement && box.parentElement.querySelector(".rn-check-label");
    const checked = box.getAttribute("data-checked") === "1";
    if (checked) {
      box.removeAttribute("data-checked");
      box.textContent = "";
      box.style.background = "transparent";
      if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
    } else {
      box.setAttribute("data-checked", "1");
      box.textContent = "✓";
      box.style.background = "#d4789a";
      if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
    }
    onSave(e.currentTarget.innerHTML);
  };
  const notesBlock = (txt, onSave) => (
    <div style={{ marginTop: 2 }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Notes</span>
      <div className="clarity-no-scrollbar clarity-notes-view" style={{ fontSize: 14, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: "12px 16px", maxHeight: 120, overflowY: "auto", scrollbarWidth: "none", lineHeight: 1.5 }}
        onClick={onSave ? (e) => handleNotesClick(e, onSave) : undefined}
        dangerouslySetInnerHTML={{ __html: txt }} />
    </div>
  );
  const repeatStr = (it) => it.repeat && it.repeat !== "None"
    ? `Repeats ${it.repeat}${it.repeat === "Weekly" && it.repeatDays?.length > 0 ? ` · ${it.repeatDays.sort((a,b)=>a-b).map(i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]).join(", ")}` : ""}${it.repeat === "Monthly" && it.repeatDates?.length > 0 ? ` · ${it.repeatDates.sort((a,b)=>a-b).join(", ")}` : ""}`
    : null;

  let content = null;

  if (type === "task") {
    const cat = categories.find(c => c.id === item.goalId || c.id === item.categoryId);
    const liveTask = tasks.find(t => t.id === item.id) || item;
    const pColor = PRIORITY_COLORS[liveTask.priority] || "#d4789a";
    const liveTaskDone = isTaskDoneOn(liveTask, date, taskOccurrenceOverrides);
    content = (
      <div style={cardStyle}>
        {topRow(CheckSquare, "Task", "#d4789a")}
        {/* Title + priority */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", flex: 1, lineHeight: 1.4, textDecoration: liveTaskDone ? "line-through" : "none", opacity: liveTaskDone ? 0.55 : 1, wordBreak: "break-word" }}>{liveTask.title}</span>
          {liveTask.priority && <span style={{ background: pColor + "22", color: pColor, borderRadius: 8, padding: "3px 10px", fontSize: 11, fontWeight: 800, textTransform: "capitalize", flexShrink: 0, marginTop: 2 }}>{liveTask.priority}</span>}
        </div>
        {/* Actions, shared with the Week view's panel */}
        <ItemActionButtons type="task" done={liveTaskDone}
          onToggleDone={() => toggleTaskDoneOn(liveTask, date, setTasks, setTaskOccurrenceOverrides)}
          onEdit={() => onEdit(liveTask, "task")} onSkip={() => onSkip(liveTask, "task", date)} onDelete={() => onDelete(liveTask, "task")} />
        {/* Meta */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {liveTask.startDate && metaRow(CalendarDays, `Start: ${liveTask.startDate}${liveTask.time ? " · " + liveTask.time + (liveTask.endTime ? " – " + liveTask.endTime : "") : ""}`)}
          {liveTask.dueDate && metaRow(Clock, `Due: ${liveTask.dueDate}${liveTask.dueTime ? `, ${liveTask.dueTime}` : ""}`)}
          {liveTask.location && metaRow(MapPin, liveTask.location)}
          <ReminderToggleRow item={liveTask} onToggle={(on) => setTasks(p => p.map(t => t.id === liveTask.id ? setItemRemindersOn(t, on) : t))} />
          {cat && metaRow(Tag, <><CategoryIcon cat={cat} size={12} /> {cat.title}</>)}
          {liveTask.subtasks?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <p style={{ ...S.label, fontSize: 13, marginBottom: 6 }}>SUBTASKS</p>
              <div className="clarity-no-scrollbar" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto", scrollbarWidth: "none", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: 8 }}>
                {liveTask.subtasks.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 4px" }}>
                    <HabitCheckbox checked={!!s.done} color="#d4789a" size={20} burst={false}
                      onChange={() => setTasks(p => p.map(t => t.id === liveTask.id ? { ...t, subtasks: (t.subtasks || []).map(x => x.id === s.id ? { ...x, done: !x.done } : x) } : t))} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.5 : 1, wordBreak: "break-word" }}>{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {liveTask.embeds?.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <p style={{ ...S.label, fontSize: 13, marginBottom: 6 }}>EMBEDDED LINKS</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {liveTask.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
              </div>
            </div>
          )}
          {liveTask.notes && notesBlock(liveTask.notes, (html) => setTasks(p => p.map(t => t.id === liveTask.id ? { ...t, notes: html } : t)))}
          {Array.isArray(liveTask.attachments) && liveTask.attachments.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <p style={{ ...S.label, marginBottom: 6 }}>ATTACHMENTS</p>
              <AttachmentViewer items={liveTask.attachments} />
            </div>
          )}
        </div>
      </div>
    );
  } else if (type === "event") {
    const liveEvent = calEvents.find(e => e.id === item.id) || item;
    const rep = repeatStr(liveEvent);
    const cat = categories.find(c => c.id === liveEvent.categoryId);
    const dur = durationLabel(liveEvent.time, liveEvent.endTime);
    // A Google Task synced in from Google Calendar (see src/google/googleTask.js).
    const fromGoogleTasks = isGoogleTaskEvent(liveEvent);
    // The occurrence this panel is about, and whether it sits in Passed
    // (utils/eventStatus.js). A Google Task's `passed` is its done tick.
    const evDate = date || liveEvent.date;
    const evDone = fromGoogleTasks ? !!liveEvent.passed : isEventPassed(liveEvent, evDate, new Date());
    content = (
      <div style={cardStyle}>
        {fromGoogleTasks
          ? topRow(CheckSquare, "Task", eventColor(liveEvent) || "#d4789a")
          : topRow(CalendarDays, "Event", eventColor(liveEvent) || "#d4789a")}
        {/* Title with color dot */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: eventColor(liveEvent), flexShrink: 0, marginTop: 5 }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", flex: 1, lineHeight: 1.4, textDecoration: evDone ? "line-through" : "none", opacity: evDone ? 0.55 : 1, wordBreak: "break-word" }}>{liveEvent.title}</span>
        </div>
        {/* Actions, shared with the Week view's panel */}
        <ItemActionButtons type={fromGoogleTasks ? "task" : "event"} done={evDone}
          onToggleDone={() => fromGoogleTasks
            ? setCalEvents(p => p.map(e => e.id === liveEvent.id ? { ...e, passed: !e.passed } : e))
            : eventStatusTap(liveEvent, evDate, { setCalEvents, openUpcoming: () => setUpcomingFor(liveEvent) })}
          onEdit={() => onEdit(liveEvent, "event")} onSkip={() => onSkip(liveEvent, "event", date)}
          onDelete={() => (fromGoogleTasks ? setGTaskNotice(true) : onDelete(liveEvent, "event"))} />
        {gTaskNotice && <GoogleTaskDeleteNotice url={googleTaskUrl(liveEvent)} onClose={() => setGTaskNotice(false)} />}
        {/* Meta */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {liveEvent.date && metaRow(CalendarDays, `${liveEvent.date}${liveEvent.time ? " · " + liveEvent.time + (liveEvent.endTime ? " – " + liveEvent.endTime : "") : ""}${dur ? " (" + dur + ")" : ""}`)}
          {liveEvent.location && metaRow(MapPin, liveEvent.location)}
          <ReminderToggleRow item={liveEvent} onToggle={(on) => setCalEvents(p => p.map(e => e.id === liveEvent.id ? setItemRemindersOn(e, on) : e))} />
          {cat && metaRow(Tag, <><CategoryIcon cat={cat} size={12} /> {cat.title}</>)}
          {rep && metaRow(RefreshCw, rep)}
          {liveEvent.embeds?.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <p style={{ ...S.label, fontSize: 13, marginBottom: 6 }}>EMBEDDED LINKS</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {liveEvent.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
              </div>
            </div>
          )}
          {fromGoogleTasks && <GoogleTaskLink url={googleTaskUrl(liveEvent)} />}
          {liveEvent.description && !fromGoogleTasks && notesBlock(liveEvent.description, (html) => setCalEvents(p => p.map(e => e.id === liveEvent.id ? { ...e, description: html } : e)))}
          {liveEvent.notes && notesBlock(liveEvent.notes, (html) => setCalEvents(p => p.map(e => e.id === liveEvent.id ? { ...e, notes: html } : e)))}
          {Array.isArray(liveEvent.attachments) && liveEvent.attachments.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <p style={{ ...S.label, marginBottom: 6 }}>ATTACHMENTS</p>
              <AttachmentViewer items={liveEvent.attachments} />
            </div>
          )}
        </div>
      </div>
    );
  } else if (type === "reminder") {
    const liveReminder = calReminders.find(r => r.id === item.id) || item;
    const cat = categories.find(c => c.id === liveReminder.categoryId);
    const pColor = PRIORITY_COLORS[liveReminder.priority] || "#5bbfb5";
    const rep = repeatStr(liveReminder);
    content = (
      <div style={cardStyle}>
        {topRow(Bell, "Reminder", "#5bbfb5")}
        {/* Title + priority */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", flex: 1, lineHeight: 1.4, textDecoration: liveReminder.passed ? "line-through" : "none", opacity: liveReminder.passed ? 0.55 : 1, wordBreak: "break-word" }}>{liveReminder.title}</span>
          {liveReminder.priority && <span style={{ background: pColor + "22", color: pColor, borderRadius: 8, padding: "3px 10px", fontSize: 11, fontWeight: 800, textTransform: "capitalize", flexShrink: 0, marginTop: 2 }}>{liveReminder.priority}</span>}
        </div>
        {/* Actions, shared with the Week view's panel */}
        <ItemActionButtons type="reminder" done={liveReminder.passed}
          onToggleDone={() => setCalReminders(p => p.map(r => r.id === liveReminder.id ? { ...r, passed: !r.passed } : r))}
          onEdit={() => onEdit(liveReminder, "reminder")} onSkip={() => onSkip(liveReminder, "reminder", date)} onDelete={() => onDelete(liveReminder, "reminder")} />
        {/* Meta */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {liveReminder.date && metaRow(Bell, `${liveReminder.date}${liveReminder.time ? " · Notify at " + liveReminder.time + (liveReminder.endTime ? " – " + liveReminder.endTime : "") : ""}`)}
          {rep && metaRow(RefreshCw, rep)}
          {cat && metaRow(Tag, <><CategoryIcon cat={cat} size={12} /> {cat.title}</>)}
          {liveReminder.notes && notesBlock(liveReminder.notes, (html) => setCalReminders(p => p.map(r => r.id === liveReminder.id ? { ...r, notes: html } : r)))}
        </div>
      </div>
    );
  }

  if (!content) return null;

  return createPortal(
    <>
      {/* Same blur backdrop as every other details panel (DETAIL_BACKDROP). */}
      {/* The pop and the dim's fade wait until the panel has been drawn (sheet-enter-wait,
          like the bottom sheets), so they run on a free main thread instead of inside the
          render that opened it; the gradient backgrounds' frosted tab bar stops re-blurring
          while it plays (Jade, 2026-11-02: laggy opening from the List view). */}
      <div className={animate ? "clarity-fade sheet-enter-wait" : undefined} ref={animate ? holdSheetEnter : undefined} onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: zIndexBase, ...DETAIL_BACKDROP }} />
      <div className={animate ? "clarity-pop-c sheet-enter-wait" : undefined} ref={animate ? holdSheetEnter : undefined} onClick={e => e.stopPropagation()}
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: zIndexBase + 1, width: "min(420px, 92vw)",
          maxHeight: "85vh", overflowY: "auto",
          display: "flex", justifyContent: "center",
        }}>
        {content}
      </div>
      {upcomingFor && (
        <MarkUpcomingPanel title={upcomingFor.title} date={date || upcomingFor.date} weekStart={weekStart}
          onClose={() => setUpcomingFor(null)}
          onKeep={() => { applyKeepUpcoming(upcomingFor, date || upcomingFor.date, setCalEvents); setUpcomingFor(null); }}
          onMove={(toDate) => {
            applyMoveEvent(upcomingFor, date || upcomingFor.date, toDate, calEventOccurrenceOverrides, setCalEvents, setCalEventOccurrenceOverrides);
            setUpcomingFor(null);
            onClose(); // this panel was about the old date
          }} />
      )}
    </>,
    (document.getElementById("portal-root") || document.body)
  );
}
